<template>
  <ValidationObserver
    ref="dispatchObserver"
    :class="{ 'spacing-px-15': !isMobile }"
    tag="section"
  >
    <b-loading
      data-cy="LOADING"
      :isFullPage="false"
      :active="process.automatic_processing_starting"
    />
    <TimelineItem
      :title="General.TO"
      stepValue="1"
      statusSize="large"
      :statusColor="`${!capacityTo[0] ? 'default' : 'success'}`"
    >
      <ValidationProvider
        v-slot="{ errors }"
        :rules="['emptyField', General.SELECT_PART].join(':')"
      >
        <b-field
          expanded
          data-cy="TO_FIELD"
          :type="errors.length ? 'is-refused' : ''"
        >
          <b-tooltip
            multilined
            class="tooltip-to"
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            size="is-large"
            position="is-top"
            :active="Boolean(dispatchInitialFile) || !completeLoad"
            role="tooltip"
          >
            <template v-slot:content>
              <label class="has-text-weight-semibold is-flex">{{
                App.MenuMain.HELP
              }}</label>
              <p class="has-text-left">
                {{
                  General.TEXTS.DESCRIPTION_TO.replace(
                    "(TYPE)",
                    General.DISPATCH.toLowerCase()
                  )
                }}
              </p>
            </template>
            <CustomTagInput
              ref="capacityTo"
              v-model="capacityTo"
              :data="hierarchyCapacities(filterTagTo)"
              autocomplete
              field="id"
              :placeholder="General.PLACEHOLDER.SECTOR"
              rounded
              :disabled="Boolean(dispatchInitialFile) || !completeLoad"
              :hasCounter="false"
              class="has-tag-ellipsis"
              role="combobox"
              maxtags="1"
              @typing="setFilterTagTo"
              @input="option => onSelectedTo(option)"
            >
              <template slot-scope="props">
                <div class="is-flex">
                  <span class="has-text-weight-semibold">
                    {{ props.option.entity.abbreviation }}
                  </span>
                  <span>{{ getCapacityTo(props) }}</span>
                  <div
                    v-if="props.option.core_protocol"
                    class="has-text-weight-semibold ml-5"
                  >
                    ({{ General.CORE_PROTOCOL.toUpperCase() }})
                  </div>
                </div>
              </template>
              <template #selected="{ tags }">
                <b-tag
                  v-for="(tag, index) in tags"
                  :key="index"
                  rounded
                  closable
                  :aria-close-label="
                    `${General.ARIA_LABEL.DELETE_FIELD} ${tag.name}`
                  "
                  :disabled="Boolean(dispatchInitialFile) || !completeLoad"
                  @close="
                    $refs.capacityTo.$children[0].removeTag(index, $event)
                  "
                >
                  <div class="is-flex is-align-items-center">
                    <b-tooltip
                      :label="
                        isDestinationInternal(tag.entity.id)
                          ? General.INTERNAL_PROCESSING
                          : General.EXTERNAL_PROCESSING
                      "
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      role="tooltip"
                      size="is-small"
                      position="is-top"
                      multilined
                    >
                      <b-icon
                        class="is-flex spacing-mx-5"
                        :icon="
                          isDestinationInternal(tag.entity.id)
                            ? 'alpha-i-circle'
                            : 'alpha-e-circle'
                        "
                        :type="
                          isDestinationInternal(tag.entity.id)
                            ? 'is-primary'
                            : 'is-warning'
                        "
                      />
                    </b-tooltip>
                    <b-tooltip
                      :label="tag.name"
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      :size="isMobile ? 'is-small' : 'is-large'"
                      role="tooltip"
                      position="is-bottom"
                      multilined
                    >
                      <div class="tag-text is-flex">
                        <span class="has-text-weight-semibold">
                          {{ tag.entity.abbreviation }}
                        </span>
                        <span class="has-ellipsis">
                          {{
                            `/${
                              tag.abbreviation.includes("/")
                                ? tag.abbreviation.split("/")[1]
                                : tag.abbreviation
                            } - ${tag.description}`
                          }}
                        </span>
                      </div>
                    </b-tooltip>
                  </div>
                </b-tag>
              </template>
              <template slot="empty">
                <p v-if="!isLoading">
                  {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
                </p>
                <b-loading v-else :isFullPage="false" :active="isLoading" />
              </template>
            </CustomTagInput>
          </b-tooltip>
          <template #message>
            <ErrorField :errors="errors" />
          </template>
        </b-field>
      </ValidationProvider>
    </TimelineItem>

    <TimelineItem
      :title="App.ProcessDetailsScreen.ORDERS"
      stepValue="2"
      statusSize="large"
      :statusColor="`${!dispatchInitialFile ? 'default' : 'success'}`"
    >
      <div class="columns">
        <ValidationProvider
          v-slot="{ errors }"
          :rules="['emptyField', General.SELECT_TYPE].join(':')"
        >
          <b-field :type="errors.length ? 'is-refused' : ''">
            <b-dropdown
              v-model="dispatchDocumentType"
              data-cy="DISPATCH_TYPE_FIELD"
              :mobileModal="false"
              scrollable
              :maxHeight="200"
              :disabled="Boolean(dispatchInitialFile)"
              role="listbox"
              class="column is-narrow dropdown-field"
            >
              <b-button
                slot="trigger"
                slot-scope="{ active }"
                class="document-type-select"
                type="is-secondary"
                :iconRight="active ? 'chevron-up' : 'chevron-down'"
                outlined
                role="button"
              >
                <label>{{
                  dispatchDocumentTypeName || General.SELECT_DOCUMENT_TYPE
                }}</label>
              </b-button>
              <b-dropdown-item
                v-for="(label, index) in hierarchyFileTypes"
                :key="index"
                :value="label"
                aria-role="listitem"
              >
                <span class="has-text-weight-semibold">{{
                  label ? label.type : ""
                }}</span>
              </b-dropdown-item>
            </b-dropdown>
            <template #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>
        <div class="column is-narrow">
          <b-button
            data-cy="CREATE_DISPATCH"
            :type="
              Boolean(dispatchInitialFile)
                ? initialFileButtonDisabledColor
                : initialFileButtonEnabledColor
            "
            :disabled="
              Boolean(dispatchInitialFile) ||
                !dispatch.capacity_id ||
                !(dispatchDocumentType && dispatchDocumentType.id)
            "
            expanded
            role="button"
            :aria-description="General.ARIA_LABEL.DISPATCH_PRIMARY"
            @click="documentEditor(true, dispatchDocumentType.id)"
          >
            <span class="has-text-weight-semibold">
              {{ General.CREATE }}
            </span>
          </b-button>
        </div>
      </div>

      <div class="spacing-mt-25 has-text-weight-semibold spacing-mb-10">
        <DisplayAttachedDoc
          v-if="dispatchInitialFile"
          :process="process"
          :file="dispatchInitialFile"
          :typeDocument="getDocumentType(dispatchInitialFile.document_type)"
          initialDoc
          :showSignAlertModal="!isSignatureDone"
        />
      </div>
    </TimelineItem>

    <TimelineItem
      stepValue="3"
      statusSize="large"
      :statusColor="
        `${!getModifiedDispatchFiles.length ? 'default' : 'success'}`
      "
    >
      <template #title>
        <label class="tabs-title spacing-px-10 is-size-5">
          {{ General.DOCUMENTS }}
        </label>
        <span class="optional-label has-text-weight-normal is-size-5">{{
          General.OPTIONAL
        }}</span>
      </template>
      <ProcessDetailDocumentDispatch
        :showModalUpload="showModalUpload"
        :documentEditor="documentEditor"
        :editableDocument="editableDocument"
        :isSignatureDone="isSignatureDone"
        :nup="process.nup"
        :editFile="editFile"
      />
    </TimelineItem>

    <TimelineItem
      stepValue="4"
      statusSize="large"
      :statusColor="`${processReferenceSelected ? 'success' : 'default'}`"
      hideProgressLine
    >
      <div
        class="is-6 has-text-weight-semibold is-flex is-align-items-center"
        :class="{ 'ml-10 mb-10': isMobile }"
      >
        {{ App.ProcessOpeningScreen.PROCESS_REFERENCE }}
        <span class="optional-label has-text-weight-bold ml-5">{{
          General.OPTIONAL
        }}</span>
        <b-tooltip
          multilined
          :type="isContrast ? 'is-white' : 'is-blue-oil'"
          :size="isMobile ? 'is-small' : 'is-large'"
          :position="isMobile ? 'is-left' : 'is-right'"
          role="tooltip"
        >
          <template v-slot:content>
            <label class="has-text-weight-semibold is-flex">{{
              App.MenuMain.HELP
            }}</label>
            <p class="has-text-left">
              {{ General.TEXTS.DESCRIPTION_NUP }}
            </p>
          </template>
          <div class="is-flex">
            <b-icon
              class="spacing-ml-10"
              :type="isContrast ? 'is-white' : 'is-blue-oil'"
              size="is-small"
              icon="information"
            />
          </div>
        </b-tooltip>
      </div>

      <div class="columns mt-5">
        <CustomTagInput
          v-model="process_reference"
          data-cy="DISPATCH_REFERENCE_INPUT"
          autocomplete
          :beforeAdding="beforeAddingProcessReference"
          :data="getReferenceList"
          field="nup_mask"
          :placeholder="General.PLACEHOLDER.SEARCH_NUP"
          name="processReference"
          class="column is-6"
          rounded
          role="combobox"
          @input="option => selectProcessReference(option)"
          @typing="setProcessReference"
        >
          <template #selected="props">
            <b-tag
              v-for="(tag, index) in props.tags"
              :key="index"
              rounded
              :tabstop="false"
              closable
              class="tag-custom is-clickable"
              @close="removeProcessReference(props.tags, index)"
            >
              <span @click="setDetailReferenceProcess(tag.nup)">{{
                applyMaskNUP(tag.nup)
              }}</span>
            </b-tag>
          </template>
          <template slot="empty">
            <p v-if="!isLoading">
              {{ General.MESSAGES.NO_NUP_FOUND.toUpperCase() }}
            </p>
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </template>
        </CustomTagInput>
        <div class="column">
          <span
            v-if="processReferenceSelected.subject"
            data-cy="DISPATCH_REFERENCE_INFO"
            class="process-reference-tag"
            :class="{ 'mt-5': isMobile }"
          >
            <b-icon
              type="is-blue-oil"
              size="is-small"
              icon="alert-circle-outline"
            />
            <div>
              <div class="custom-size">
                <div class="custom-width">
                  <label class="has-text-weight-semibold mr-5"
                    >{{ General.SUBJECT }}:
                  </label>
                  <span>{{ processReferenceSelected.subject }}</span>
                </div>
                <span
                  class="is-clickable is-right"
                  @click="closeReferenceProcess"
                >
                  <b-icon icon="close" type="is-grey-light" size="is-small" />
                </span>
              </div>
              <p class="custom-size">
                <label class="has-text-weight-semibold"
                  >{{ General.OPENING_UNIT }}:
                </label>
                <span
                  v-if="
                    processReferenceSelected.origin &&
                      processReferenceSelected.origin.entity
                  "
                  >{{
                    `${processReferenceSelected.origin.entity.abbreviation}/${
                      processReferenceSelected.origin.abbreviation.includes("/")
                        ? processReferenceSelected.origin.abbreviation.split(
                            "/"
                          )[1]
                        : processReferenceSelected.origin.abbreviation
                    } - ${processReferenceSelected.origin.description.toLowerCase()}`
                  }}</span
                >
              </p>
              <p
                v-if="processReferenceSelected.cc.length"
                class="custom-size custom-width-400"
              >
                <span class="has-text-weight-semibold"
                  >{{ General.INTERESTED }}:
                </span>
                <span>
                  {{ getCC(processReferenceSelected.cc) }}
                </span>
              </p>
            </div>
          </span>
        </div>
      </div>
    </TimelineItem>
    <div class="mobile-buttons">
      <TramitingTooltip
        :hasMandatoryFilled="Boolean(capacityTo[0])"
        :hasPrimaryFile="Boolean(dispatchInitialFile)"
        :isInitialFileSigned="isInitialFileSigned"
        :hasSignatures="hasSignatures"
        :pendingSignatures="pendingSignaturesValue"
        :declinedSignatures="declinedSignaturesValue"
        :hasDOE="hasDOE"
        :hasDOEProgrammedSignatures="hasDOEProgrammedSignatures"
        isDispatch
        :position="isMobile ? 'is-top' : 'is-right'"
      >
        <b-button
          data-cy="PROCESS_DISPATCH"
          :class="{ 'spacing-ml-50': !isMobile }"
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          :disabled="disableProcessing"
          :expanded="isMobile"
          role="button"
          :aria-description="General.ARIA_LABEL.DISPATCH_PROCESS"
          @click="checkHasJointFilesDraft(showModalConfirm)"
        >
          <label class="is-size-6 spacing-px-5">
            {{ General.PROCEED }}
          </label>
        </b-button>
      </TramitingTooltip>
      <TramitingTooltip
        :hasMandatoryFilled="Boolean(capacityTo[0])"
        :hasPrimaryFile="Boolean(dispatchInitialFile)"
        :hasSignatures="hasSignatures"
        :pendingSignatures="pendingSignaturesValue"
        :declinedSignatures="declinedSignaturesValue"
        :hasDOE="hasDOE"
        :hasDOEProgrammedSignatures="hasDOEProgrammedSignatures"
        isAutomaticTramiting
        isDispatch
        :position="isMobile ? 'is-top' : 'is-right'"
      >
        <b-button
          data-cy="PROCESS_AUTOMATIC_DISPATCH"
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          outlined
          class="custom-button-border"
          :class="{ 'ml-15': !isMobile }"
          :disabled="disableAutomaticProcessing"
          :expanded="isMobile"
          role="button"
          @click="checkHasJointFilesDraft(openModalAutoProcess)"
        >
          <label class="is-size-6">{{
            dispatch.automatic_processing
              ? General.CANCEL_AUTO_PROCESS
              : General.AUTO_PROCESS
          }}</label>
        </b-button>
      </TramitingTooltip>
      <b-button
        data-cy="PROCESS_DISPATCH_DELETE"
        :type="isContrast ? 'is-white-dark' : 'is-orange'"
        outlined
        class="custom-button-border"
        :class="{ 'ml-15': !isMobile }"
        role="button"
        @click="confirmDelete"
      >
        <span class="is-size-6">{{ General.CANCEL }}</span>
      </b-button>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { debounce, truncate } from "lodash";

import { App, General } from "@/utils/constants";
import { NUP, applyMask } from "@/utils/protocols";
import {
  PROCESS_DETAILS_TABS as TABS,
  DOC_SIGNATURES_STATUS as DOC_SIGN,
  SIZES as sizes,
  PROCESS_FILES_MAX_SIZE,
  ATTACHEMENT_MAX_COUNT
} from "@/utils/enumerate";

import ModalAttachments from "@/views/App/ModalAttachments";
import ModalEditableDocument from "@/views/App/ModalEditableDocument.vue";
import ModalWarning from "@/views/App/ModalWarning.vue";

import TimelineItem from "@/components/TimelineItem";
import DisplayAttachedDoc from "@/components/DisplayAttachedDoc";
import TramitingTooltip from "@/components/TramitingTooltip";

import ProcessDetailDocumentDispatch from "./ProcessDetailDocumentDispatch";

export default {
  name: "DispatchProcessTab",

  components: {
    TimelineItem,
    DisplayAttachedDoc,
    ProcessDetailDocumentDispatch,
    TramitingTooltip
  },

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      App,
      General,
      DOC_SIGN,
      TABS,
      isLoading: false,
      capacityTo: [],
      filterTagTo: "",
      dispatch: {},
      dispatchDocumentType: null,
      process_reference: [],
      showDetailReferenceProcess: "",
      completeLoad: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      process: "getCurrentProcess",
      fileTypes: "getFileTypesByEditor",
      capacities: "getCapacities",
      initialFile: "getDispatchInitialFile",
      fileTypeById: "getFileTypeById",
      hierarchyCapacities: "getHierarchyCapacitiesFiltered",
      getModifiedDispatchFiles: "getModifiedDispatchFiles",
      getModifiedDispatchFilesEditable: "getModifiedDispatchFilesEditable",
      getCurrentTab: "getCurrentTabProcessDetails",
      getDispatch: "getDispatch",
      listProcess: "getListProcess",
      processSignatureHistoryType: "getProcessSignatureHistoryType",
      clearDispatch: "getClearDispatch",
      isContrast: "isContrast",
      isMobile: "isMobile",
      getCurrentJoint: "getCurrentJoint",
      joinFilesCount: "getJointFilesCount",
      getProcessFileSize: "getProcessFileSize",
      getCapacityById: "getCapacityById",
      getDraftFilesSize: "getDraftFilesSize",
      dispatchUpdater: "getDispatchUpdater",
      needUpdateAttachments: "needUpdateAttachments",
      getListUploadFiles: "getListUploadFiles"
    }),

    isSignatureDone() {
      return this.dispatchInitialFile?.sign_history?.some(
        history =>
          history.user_id !== this.user.id &&
          history.status.includes("SIGNED") &&
          !history.only_historical
      );
    },

    disableProcessing() {
      return (
        this.dispatch.automatic_processing ||
        !this.capacityTo[0] ||
        !this.dispatchInitialFile ||
        !this.isInitialFileSigned ||
        Boolean(this.pendingSignaturesValue) ||
        !this.hasDOEProgrammedSignatures ||
        Boolean(this.declinedSignaturesValue)
      );
    },

    disableAutomaticProcessing() {
      const hasPendingSignature = [
        this.dispatchInitialFile,
        ...this.getModifiedDispatchFiles
      ].some(file =>
        [this.DOC_SIGN.PENDING, this.DOC_SIGN.PARTIALLY].includes(file?.status)
      );

      return (
        (!hasPendingSignature ||
          !this.capacityTo[0] ||
          Boolean(this.declinedSignaturesValue) ||
          (this.hasDOE && !this.hasDOEProgrammedSignatures)) &&
        !this.dispatch.automatic_processing
      );
    },

    isInitialFileSigned() {
      return this.dispatchInitialFile?.can_tramit;
    },

    hasSignatures() {
      return (
        Boolean(this.dispatchInitialFile?.sign_history?.length) ||
        this.getModifiedDispatchFiles.some(file => file?.sign_history?.length)
      );
    },

    pendingSignaturesValue() {
      return this.processSignatureHistoryType(
        [...this.getModifiedDispatchFiles, this.dispatchInitialFile],
        this.DOC_SIGN.PENDING
      );
    },

    declinedSignaturesValue() {
      return this.processSignatureHistoryType(
        [...this.getModifiedDispatchFiles, this.dispatchInitialFile],
        this.DOC_SIGN.REFUSED
      );
    },

    hasDOE() {
      return this.getModifiedDispatchFilesEditable.some(
        file => file.to_publish
      );
    },

    hasDOEProgrammedSignatures() {
      return !this.getModifiedDispatchFilesEditable.some(file =>
        file.to_publish ? !file.doe_obrigatory_signature.length : false
      );
    },

    processReferenceSelected() {
      if (this.showDetailReferenceProcess && this.process_reference.length)
        return this.process_reference.find(
          process => process.nup === this.showDetailReferenceProcess
        );
      return false;
    },

    isInternalSolicitation() {
      return (
        this.user.capacity.part_id ===
        this.capacities.find(
          capacity => capacity.id === this.dispatch.capacity_id
        )?.entity?.id
      );
    },

    hierarchyFileTypes() {
      let types = this.fileTypes(4);

      if (this.isInternalSolicitation) {
        types = types.filter(type => [3, 102].includes(type.id));
      } else {
        types = types.filter(type => [2, 3, 102].includes(type.id));
      }
      return types;
    },

    dispatchDocumentTypeName() {
      return this.dispatchDocumentType?.type;
    },

    dispatchInitialFile() {
      return this.initialFile(this.dispatch.id);
    },

    initialFileButtonEnabledColor() {
      return this.isContrast ? "is-white-dark" : "is-primary";
    },

    initialFileButtonDisabledColor() {
      return this.isContrast ? "is-black" : "is-light";
    },

    getCurrentSizeProcess() {
      return this.getDraftFilesSize("dispatchFiles") + this.getProcessFileSize;
    },

    activeTab() {
      return this.getCurrentTab.value;
    },

    isDispatchTab() {
      return this.activeTab === this.TABS.ORDERS.value;
    },

    hasUploadFiles() {
      return this.getListUploadFiles.some(
        file =>
          file.dispatch_id === this.dispatch.id &&
          !file.isErro &&
          file.status === "pending"
      );
    },

    getReferenceList() {
      return this.listProcess.filter(
        process => process.id != this.$route.params.id
      );
    }
  },

  watch: {
    activeTab: {
      handler(actualTab) {
        if (actualTab === this.TABS.ORDERS.value) {
          this.getAutoDraft();
          this.requestProcessFileSize({ processId: this.id });
        }
      },
      immediate: true
    },
    dispatchUpdater() {
      if (this.isDispatchTab) this.getAutoDraft();
    },
    dispatch: {
      async handler() {
        if (!this.isLoading && this.dispatch.id) {
          const result = await this.$store.dispatch(
            "dispatchPutAutoDraft",
            this.dispatch
          );
          if (result) {
            this.process_reference = this.getFormattedProcessReferences(
              result.process_reference
            );
          }
        }
      },
      deep: true
    },

    dispatchInitialFile: {
      handler() {
        if (this.dispatchInitialFile) {
          this.dispatchDocumentType = this.hierarchyFileTypes.find(
            type => type.id === this.dispatchInitialFile.document_type
          );
        }
      },
      deep: true
    },

    clearDispatch() {
      this.cleaningDispatch();
    },

    getCurrentSizeProcess(value) {
      if (
        value > PROCESS_FILES_MAX_SIZE &&
        this.getCurrentTab.value === this.TABS.ORDERS.value
      ) {
        this.snackToast.open({
          message: this.General.TEXTS.LIMIT_PROCESS_SIZE_EXCEEDED,
          type: "is-danger",
          duration: 8000
        });
      }
    },

    needUpdateAttachments(value) {
      if (
        this.isDispatchTab &&
        value.fileObject.dispatch_id &&
        value.fileObject.process_id === this.$route.params.id
      )
        this.updateListAttachmentsAction(value);
    }
  },

  beforeDestroy() {
    this.removePrimaryFile(this.dispatch.id);
  },

  beforeMount() {
    if (this.activeTab === this.TABS.ORDERS.value && this.getCurrentJoint)
      this.getJointFile({
        jointId: this.getCurrentJoint.id,
        page: 1,
        paginator_count: 1,
        only_count: true
      });
    this.configPage();
  },

  methods: {
    ...mapMutations({
      dispatchDraftSuccess: "dispatchDraftSuccess",
      setDispatchUpload: "setDispatchUpload",
      setCurrentTab: "setCurrentTabProcessDetails",
      removePrimaryFile: "removePrimaryFile"
    }),

    ...mapActions({
      capacityHierarchyRequest: "capacityHierarchyRequest",
      setAutoProcessing: "setDispatchAutoProcessing",
      searchProcessByNup: "searchProcessByNup",
      dispatchGetDraft: "dispatchGetDraft",
      getJointFile: "getJointFile",
      requestProcessFileSize: "requestProcessFileSize",
      deleteDispatchDraft: "deleteDispatchDraftRequest",
      dispatchFileRequest: "dispatchFileRequest",
      updateListAttachmentsAction: "updateListAttachmentsAction"
    }),

    async initializeScreen() {
      await this.capacityHierarchyRequest();
    },

    async configPage() {
      this.dispatchDocumentType = this.fileTypeById(3);
      await this.initializeScreen();
    },

    async editFile(id, key, value, editable = false) {
      const editedFile = !editable
        ? this.getModifiedDispatchFiles.find(file => file.id === id)
        : this.getModifiedDispatchFilesEditable.find(file => file.id === id);

      if (editedFile[key] !== value) {
        editedFile[key] = value;
        await this.$store.dispatch("editProcessFile", {
          gdoc: editedFile,
          editable: editable
        });
      }
    },

    editableDocument(primary, fileType, editable = false) {
      if (this.isSignatureDone) {
        this.showAlertDocumentWithSignDone();
        return;
      }
      this.$buefy.modal.open({
        parent: this,
        component: ModalEditableDocument,
        props: {
          createDefault: () => this.documentEditor(primary, fileType, editable),
          processId: this.process.id,
          dispatchId: this.dispatch.id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    documentEditor(primary = false, template = 7, editable = false) {
      if (this.isSignatureDone) {
        this.showAlertDocumentWithSignDone();
        return;
      }
      this.$router.push({
        path: `/app/documento-editor/${this.process.id}?template=${template ||
          ""}&dispatchId=${this.dispatch.id}`,
        query: { isPrimary: primary, isEditable: editable }
      });
    },

    showAlertDocumentWithSignDone() {
      this.snackToast.open({
        message: this.General.MESSAGES.WARNING_DISPATCH_DOCUMENT_WITH_SIGN_DONE,
        type: "is-warning",
        duration: 8000
      });
    },

    getDocumentType(documentTypeId) {
      if (documentTypeId) return this.fileTypeById(documentTypeId).type;
      return null;
    },

    showModalConfirm() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${this.General.MESSAGES.ARE_YOU_SURE_YOU_WANT_PROCESS_ORDER}?`,
          confirm: async () => await this.submitDispatch(),
          buttons: [
            {
              label: General.PROCEED,
              isConfirm: true,
              action: true,
              dataCY: "CONFIRM_SUBMIT_DISPATCH"
            },
            {
              label: General.CANCEL,
              isConfirm: false,
              action: false,
              dataCY: "CANCEL_SUBMIT_DISPATCH"
            }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showModalUpload(isEditable = false) {
      if (this.isSignatureDone) {
        this.showAlertDocumentWithSignDone();
        return;
      }

      this.$buefy.modal.open({
        parent: this,
        component: ModalAttachments,
        props: {
          dispatchId: this.dispatch.id,
          editable: isEditable,
          currentSizeProcess: this.getCurrentSizeProcess,
          currentProcessId: this.process.id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async submitDispatch() {
      if (this.validateDispatch()) {
        this.isLoading = true;
        const status = await this.$store.dispatch(
          "dispatchProcess",
          this.dispatch
        );
        if (status) {
          this.cleaningDispatch();
          this.setCurrentTab(this.TABS.BASIC_DATA.value);
        }
        this.isLoading = false;
      }
    },

    cleaningDispatch() {
      this.dispatch = {};
      this.capacityTo = [];
      this.$router.push("/app/processos");
    },

    async requestProcessFileList() {
      this.dispatchFileRequest({
        dispatchId: this.getDispatch.id,
        editable: true,
        page: 1,
        paginator_count: ATTACHEMENT_MAX_COUNT
      });
      await this.dispatchFileRequest({
        dispatchId: this.getDispatch.id,
        page: 1,
        paginator_count: ATTACHEMENT_MAX_COUNT
      });
      this.completeLoad = true;
    },

    async getAutoDraft() {
      await this.dispatchGetDraft(this.id).then(() => {
        if (this.getDispatch.process_id === this.id) {
          this.dispatch = this.getDispatch;
        } else {
          this.dispatch = {};
          this.dispatchDraftSuccess(this.dispatch);
        }

        if (this.getDispatch.id) {
          this.setDispatchUpload(this.dispatch.id);
          this.requestProcessFileList();
          this.prepareCapacityDestination();
        }
        this.process_reference = this.getFormattedProcessReferences(
          this.dispatch.process_reference
        );
      });
      if (this.dispatch?.automatic_processing_cancelled) {
        this.snackToast.open({
          message: this.General.MESSAGES.WARNING_AUTOMATIC_PROCESSING_CANCELLED,
          type: "is-danger",
          duration: 8000
        });
      }
    },

    onSelectedTo(option) {
      if (option.length) {
        this.dispatch.capacity_id = option[0].id;
      } else {
        this.dispatch.capacity_id = null;
      }
      this.dispatchDocumentType = this.fileTypeById(3);
    },

    setFilterTagTo(text) {
      this.filterTagTo = text;
    },

    setProcessReference: debounce(async function(search_text) {
      await this.searchProcessByNup(search_text.replace(/\D/g, ""));
    }, 500),

    prepareCapacityDestination() {
      const capacity = this.getCapacityById(this.dispatch.capacity_id);
      if (capacity)
        this.capacityTo = [
          {
            ...capacity,
            name: `${capacity.entity.abbreviation}/${
              capacity.abbreviation.includes("/")
                ? capacity.abbreviation.split("/")[1]
                : capacity.abbreviation
            } - ${capacity?.description}`
          }
        ];
    },

    validateDispatch() {
      let msg = "";
      if (!this.dispatch.capacity_id) msg = this.General.TEXTS.EMPTY_TO;
      else if (!this.dispatchInitialFile)
        msg = this.General.TEXTS.EMPTY_DISPATCH;

      if (msg.length) {
        this.snackToast.open({
          message: `${msg}.`,
          type: "is-danger",
          duration: 8000
        });
      }

      return !msg.length;
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    selectProcessReference(options) {
      this.showDetailReferenceProcess = options[options.length - 1].nup;
      this.dispatch = {
        ...this.dispatch,
        process_reference_list: options.map(process => process.id)
      };
    },

    removeProcessReference(options, index) {
      this.process_reference = options.filter(
        (_, index_process) => index_process !== index
      );
      this.showDetailReferenceProcess =
        this.process_reference.length > 0
          ? this.process_reference[this.process_reference.length - 1].nup
          : "";
      this.dispatch = {
        ...this.dispatch,
        process_reference_list: this.process_reference.map(
          process => process.id
        )
      };
    },

    beforeAddingProcessReference(option) {
      return !this.process_reference.find(process => process.id === option.id);
    },

    setDetailReferenceProcess(nup) {
      this.showDetailReferenceProcess = nup;
    },

    closeReferenceProcess() {
      this.showDetailReferenceProcess = "";
    },

    getCC(list) {
      return `${list
        .slice(0, 2)
        .map(cc => {
          return `${cc.user_name || cc.abbreviation}${
            cc.user_cpf ? ` - ${cc.user_cpf}` : ` - ${cc.description}`
          }`;
        })
        .join(", ")} ${list.length > 2 ? General.TEXTS.AND_OTHERS : ""}`;
    },

    async openModalAutoProcess() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${
            this.dispatch.automatic_processing
              ? this.General.MESSAGES.WARNING_CANCEL_AUTO_PROCESS
              : this.General.MESSAGES.WARNING_AUTO_PROCESS
          }?`,
          confirm: async () => {
            this.isLoading = true;
            const result = await this.setAutoProcessing({
              dispatchId: this.dispatch.id,
              automatic: !this.dispatch.automatic_processing
            });
            if (result) {
              await this.getAutoDraft();
              this.isLoading = false;
            }
          },
          buttons: [
            {
              label: this.dispatch.automatic_processing
                ? General.YES
                : General.AUTO_PROCESS,
              isConfirm: true,
              action: true
            },
            {
              label: this.dispatch.automatic_processing
                ? General.NO
                : General.CANCEL,
              isConfirm: false,
              action: false
            }
          ],
          invertOutline: !this.dispatch.automatic_processing
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    isDestinationInternal(partId) {
      return this.user.capacity.part_id === partId;
    },

    getCapacityTo(props) {
      const truncateLength = this.mobileSize === sizes.IS_SMALL ? 15 : 30;
      if (this.isMobile && props.option.core_protocol)
        return truncate(
          `/${
            props.option.abbreviation.includes("/")
              ? props.option.abbreviation.split("/")[1]
              : props.option.abbreviation
          } - ${props.option.description}`,
          { length: truncateLength }
        );

      return `/${
        props.option.abbreviation.includes("/")
          ? props.option.abbreviation.split("/")[1]
          : props.option.abbreviation
      } - ${props.option.description}`;
    },

    getFormattedProcessReferences(referenceList) {
      return (
        referenceList?.map(reference => ({
          ...reference,
          origin: { ...this.getCapacityById(reference.origin) },
          cc: reference.cc.map(item => {
            const capacity = this.getCapacityById(item.capacity_id);
            return {
              ...item,
              abbreviation: capacity?.abbreviation,
              description: capacity?.description,
              entity: capacity?.entity
            };
          })
        })) ?? []
      );
    },

    confirmDelete() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: this.General.MESSAGES
            .ARE_YOU_SURE_YOU_WANT_CANCEL_DISPATCH_DRAFT,
          confirm: () => {
            this.deleteDispatchDraft(this.dispatch.id).then(async () => {
              this.dispatch = {};
              this.capacityTo = [];
              this.isLoading = true;
              this.getAutoDraft();
              this.isLoading = false;
              this.$refs.dispatchObserver.reset();
            });
          },
          buttons: [
            {
              label: General.YES,
              isConfirm: true,
              action: true,
              dataCY: "CONFIRM_DELETE_DISPATCH"
            },
            {
              label: General.NO,
              isConfirm: false,
              action: false,
              dataCY: "CANCEL_DELETE_DISPATCH"
            }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    checkHasJointFilesDraft(actionFunction) {
      if (this.hasUploadFiles)
        return this.snackToast.open({
          message: General.TEXTS.WAIT_FOR_ALL_FILES_TO_LOAD,
          position: "is-top",
          type: "is-danger",
          duration: 8000
        });

      if (!this.joinFilesCount) return actionFunction();
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: this.General.WARNING,
          message: this.General.TEXTS.CONFIRM_CONTINUE_IGNORE_JOINT,
          confirm: actionFunction,
          invertOutline: true,
          buttons: [
            {
              label: General.YES,
              isConfirm: true,
              action: true,
              dataCY: "CONFIRM_CONTINUE_DISPATCH"
            },
            {
              label: General.NO,
              isConfirm: false,
              action: false,
              dataCY: "CANCEL_CONTINUE_DISPATCH"
            }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.tooltip-to {
  width: 100%;
}

.document-type-select {
  display: flex;
  width: 330px;
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;
  @media screen and (max-width: $tablet) {
    width: 100%;
  }
}

.optional-label {
  font-size: 10px;
  font-weight: 300;
  color: #888;
}

.dropdown-field {
  button {
    background-color: transparent;
    color: $color-7;
  }
}

.mobile-buttons {
  display: flex;

  @media screen and (max-width: $tablet) {
    flex-direction: column;
    gap: 10px;
  }
}

.tag-text {
  max-width: 65vw;
  @media screen and (max-width: 768px) {
    max-width: 60vw;
  }
  @media screen and (max-width: 350px) {
    max-width: 50vw;
  }
}
</style>
